'use client';

import { fundingFeedbackPresentation } from '@morfi/onramp-sdk';
import type { FundingCheckout } from '@morfi/onramp-sdk/react';
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useRef } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { TOAST_IDS } from '@/config/toast-ids';
import { showToast } from '@/lib/ui/toast';
import { FundingStatusHeader } from '../history/funding-status-header';

type FeedbackCheckout = Pick<
  FundingCheckout,
  | 'error'
  | 'state'
  | 'providerFailure'
  | 'returningMethodUnavailable'
  | 'preview'
  | 'busy'
  | 'changeMethod'
  | 'connect'
  | 'retry'
>;

export function FundingCheckoutFeedback({ checkout }: { checkout: FeedbackCheckout }): ReactNode {
  const t = useTranslations('onramp');
  const feedback = fundingFeedbackPresentation(checkout);
  const transientMessage = feedback && !feedback.title ? feedback.message : null;
  const announcedError = useRef<string | null>(null);
  useEffect(() => {
    if (transientMessage && announcedError.current !== transientMessage) {
      showToast({
        id: TOAST_IDS.ONRAMP_CHECKOUT_ERROR,
        message: t(transientMessage),
        variant: 'error',
      });
    }
    announcedError.current = transientMessage;
  }, [transientMessage, t]);
  if (!feedback || (!feedback.title && !feedback.action)) return null;
  return (
    <div className="flex flex-col gap-4.5">
      {feedback.title && feedback.artwork ? (
        <FundingStatusHeader
          artwork={feedback.artwork}
          title={t(feedback.title)}
          description={t(feedback.message)}
        />
      ) : null}
      {feedback.action ? (
        <AnimatedButton
          className="h-11 rounded-control"
          loading={checkout.busy}
          disabled={checkout.busy}
          onClick={checkout[feedback.action]}
        >
          {t(feedback.actionLabel)}
        </AnimatedButton>
      ) : null}
    </div>
  );
}
